import './styles'

import {useCallback, useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useOnClickOutside} from '@vio/atlas/hooks/useOnClickOutside'
import noop from 'lodash/noop'

import {isFlutterAppWebView} from '@daedalus/core/src/_web/utils/flutter/isFlutterAppWebView'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  TrackEventPayload
} from '@daedalus/core/src/analytics/types/Events'
import {isReactNativeWebView} from '@daedalus/core/src/native'

import {
  CookieBannerClickComponent,
  getCookieConsentComponent,
  getCookieConsentContext,
  storeCookieConsentTimestamp
} from './utils'

export interface Props {
  /**
   * Callback invoked when a user accepts the cookies, either in the
   * banner or the preference center
   */
  onAccept?: (acceptedByUser: boolean) => void
  trackEvent: (payload: TrackEventPayload) => void
}

export const useOsanoCookieConsent = ({
  onAccept: setCookiesAcceptedByUser = noop,
  trackEvent
}: Props) => {
  const {locale} = useIntl()
  const isEnabled = !isReactNativeWebView() && !isFlutterAppWebView()
  const dialogRef = useRef<HTMLElement | null>(
    document?.querySelector('.osano-cm-dialog') as HTMLElement
  )

  const handleCookieConsentScriptLoaded = useCallback(() => {
    trackEvent({
      category: Category.System,
      entity: Entity.CookieBannerScript,
      action: Action.Loaded
    })
  }, [trackEvent])

  const trackBannerSubmitted = useCallback(
    (component?: CookieBannerClickComponent) => {
      // The cookie consent id is not updated immediately when the user clicks on the accept button
      // so we need to wait for it to be updated in localStorage
      setTimeout(() => {
        const cookieConsentContext = getCookieConsentContext()

        trackEvent?.({
          category: Category.User,
          entity: Entity.CookieBanner,
          action: Action.Submitted,
          analyticsContext: {
            [AnalyticsContext.CookieConsentContext]: cookieConsentContext
          },
          component
        })
      }, 500)
    },
    [trackEvent]
  )

  const trackBannerShown = useCallback(() => {
    const cookieConsentContext = getCookieConsentContext()

    trackEvent?.({
      category: Category.System,
      entity: Entity.CookieBanner,
      action: Action.Displayed,
      analyticsContext: {
        [AnalyticsContext.CookieConsentContext]: cookieConsentContext
      }
    })
  }, [trackEvent])

  const onAccept = useCallback(() => {
    storeCookieConsentTimestamp()
    setCookiesAcceptedByUser(true)
  }, [setCookiesAcceptedByUser])

  const onDeny = useCallback(() => {
    setCookiesAcceptedByUser(false)
  }, [setCookiesAcceptedByUser])

  const handleOsanoConsentSaved = useCallback(
    consent => {
      if (window?.Osano?.cm?.drawerOpen) {
        trackBannerSubmitted(CookieBannerClickComponent.PreferenceCenter)
        onAccept()
      } else if (window?.Osano?.cm?.dialogOpen) {
        const component = getCookieConsentComponent(consent)
        if (component === CookieBannerClickComponent.AcceptButton) {
          onAccept()
        } else {
          onDeny()
        }

        trackBannerSubmitted(component)
      }
    },
    [trackBannerSubmitted, onAccept, onDeny]
  )

  // Track the cookie consent script loaded event from Osano
  useEffect(() => {
    window.Osano?.cm?.addEventListener(
      'osano-cm-initialized',
      handleCookieConsentScriptLoaded
    )

    return () => {
      window.removeEventListener(
        'osano-cm-initialized',
        handleCookieConsentScriptLoaded
      )
    }
  }, [handleCookieConsentScriptLoaded])

  // Deny all cookies when the user clicks outside the cookie banner
  useOnClickOutside(dialogRef, () => {
    if (window?.Osano?.cm?.dialogOpen) {
      const denyAllButton = document.querySelector(
        '.osano-cm-denyAll'
      ) as HTMLButtonElement

      // In some cases, the deny all button is not available, so the banner will not be closed.
      // Checking if the button is available prevents from triggering the AppPage event multiple times
      if (denyAllButton) {
        denyAllButton.click()
        trackBannerSubmitted(CookieBannerClickComponent.AppPage)
      }
    }
  })

  // Track the cookie banner shown event from Osano
  useEffect(() => {
    if (isEnabled && window?.Osano?.cm?.dialogOpen) trackBannerShown()
  }, [trackBannerShown, isEnabled])

  // Set the locale for the cookie banner
  useEffect(() => {
    const sanitizedLocale = locale.split('-')[0]
    if (window.Osano?.cm) {
      window.Osano.cm.locale = sanitizedLocale
    }
  }, [locale])

  // Display the cookie banner
  useEffect(() => {
    if (isEnabled && window?.Osano?.cm?.dialogOpen && dialogRef.current) {
      dialogRef.current.style.display = 'flex'
    }
  }, [isEnabled, dialogRef])

  // Track the consent saved event from Osano when the user clicks on the accept/reject buttons
  // it is triggered when the user clicks on the accept button in the preference center
  useEffect(() => {
    window?.Osano?.cm?.addEventListener(
      'osano-cm-consent-saved',
      handleOsanoConsentSaved
    )

    return () => {
      window?.Osano?.cm?.removeEventListener(
        'osano-cm-consent-saved',
        handleOsanoConsentSaved
      )
    }
  }, [handleOsanoConsentSaved])
}
